import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useVaults } from '../hooks/useVaults';

interface GraphNode {
  id: string;
  name: string;
  type: 'vault' | 'doc';
  vaultId?: string | null;
  size?: number;
  status?: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  radius: number;
}

interface GraphLink {
  source: string;
  target: string;
}

export function KnowledgeGraphPage() {
  const navigate = useNavigate();
  const { vaults } = useVaults();
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [filterVault, setFilterVault] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    setLoading(true);
    api.listDocuments()
      .then(res => setDocuments(res.documents || []))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  // Compute graph nodes and links
  const { nodes, links } = useMemo(() => {
    const rawNodes: GraphNode[] = [];
    const rawLinks: GraphLink[] = [];

    // Filter documents
    const filteredDocs = documents.filter(doc => {
      const matchVault = filterVault === 'all' || doc.vault_id === filterVault;
      const matchSearch = !searchQuery || doc.filename.toLowerCase().includes(searchQuery.toLowerCase());
      return matchVault && matchSearch;
    });

    const activeVaultIds = new Set(filteredDocs.map(d => d.vault_id).filter(Boolean));
    const relevantVaults = vaults.filter(v => activeVaultIds.has(v.id) || filterVault === v.id);

    const width = 800;
    const height = 600;
    const centerX = width / 2;
    const centerY = height / 2;

    // 1. Vault Nodes (orbital centers)
    const vaultPositions: Record<string, { x: number; y: number }> = {};
    if (relevantVaults.length === 0) {
      vaultPositions['default'] = { x: centerX, y: centerY };
      rawNodes.push({
        id: 'vault_root',
        name: 'Kho Tri Thức Tổng',
        type: 'vault',
        x: centerX,
        y: centerY,
        vx: 0,
        vy: 0,
        color: '#a855f7',
        radius: 26
      });
    } else {
      const angleStep = (2 * Math.PI) / relevantVaults.length;
      relevantVaults.forEach((v, idx) => {
        const radius = Math.min(width, height) * 0.28;
        const x = centerX + radius * Math.cos(idx * angleStep);
        const y = centerY + radius * Math.sin(idx * angleStep);
        vaultPositions[v.id] = { x, y };

        rawNodes.push({
          id: `vault_${v.id}`,
          name: v.name,
          type: 'vault',
          vaultId: v.id,
          x,
          y,
          vx: 0,
          vy: 0,
          color: '#a855f7',
          radius: 24
        });
      });
    }

    // 2. Document Nodes (orbiting around their vault)
    filteredDocs.forEach((doc, idx) => {
      const vaultId = doc.vault_id;
      const center = (vaultId && vaultPositions[vaultId]) ? vaultPositions[vaultId] : (vaultPositions['default'] || { x: centerX, y: centerY });
      
      const docAngle = (idx * 1.61803398875) * 2 * Math.PI; // golden ratio distribution
      const dist = 60 + ((idx % 5) * 24);
      const x = center.x + dist * Math.cos(docAngle);
      const y = center.y + dist * Math.sin(docAngle);

      const ext = (doc.filename.split('.').pop() || '').toLowerCase();
      let color = '#38bdf8'; // cyan default
      if (['pdf'].includes(ext)) color = '#f87171'; // red
      else if (['xlsx', 'csv'].includes(ext)) color = '#34d399'; // green
      else if (['docx', 'doc'].includes(ext)) color = '#60a5fa'; // blue
      else if (['jpg', 'png', 'webp'].includes(ext)) color = '#fbbf24'; // yellow

      rawNodes.push({
        id: doc.id,
        name: doc.filename,
        type: 'doc',
        vaultId: doc.vault_id,
        size: doc.file_size,
        status: doc.status,
        x,
        y,
        vx: 0,
        vy: 0,
        color,
        radius: 12
      });

      // Link to vault
      const targetVaultNodeId = (vaultId && relevantVaults.some(v => v.id === vaultId)) 
        ? `vault_${vaultId}` 
        : 'vault_root';

      if (rawNodes.some(n => n.id === targetVaultNodeId)) {
        rawLinks.push({
          source: targetVaultNodeId,
          target: doc.id
        });
      }
    });

    return { nodes: rawNodes, links: rawLinks };
  }, [documents, vaults, filterVault, searchQuery]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === svgRef.current || (e.target as HTMLElement).tagName === 'svg') {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prev => Math.min(Math.max(prev * delta, 0.4), 3.0));
  };

  const handleStartChatWithDoc = (docId: string) => {
    navigate('/chat');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '16px', flexDirection: 'column' }}>
        <div className="spinner" style={{ width: 32, height: 32 }} />
        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Đang dựng bản đồ tri thức 2D (Knowledge Graph)...</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '0' }}>

      {/* Page Header */}
      <div className="page-header" style={{ flexShrink: 0 }}>
        <div>
          <h1 className="page-title">🕸️ Knowledge Graph 2D — Bản Đồ Tri Thức</h1>
          <p className="page-subtitle">
            Trực quan hóa cấu trúc liên kết giữa các Kho báu dữ liệu và toàn bộ tài liệu trong kho tri thức ErixOne
          </p>
        </div>

        {/* Toolbar Controls */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <select
            className="input"
            value={filterVault}
            onChange={e => setFilterVault(e.target.value)}
            style={{ fontSize: '12.5px', padding: '6px 10px' }}
          >
            <option value="all">📁 Tất cả Kho báu ({vaults.length})</option>
            {vaults.map(v => (
              <option key={v.id} value={v.id}>📁 {v.name}</option>
            ))}
          </select>

          <input
            className="input"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="🔍 Lọc tài liệu..."
            style={{ width: '160px', fontSize: '12.5px', padding: '6px 10px' }}
          />

          <button className="btn btn--secondary btn--sm" onClick={() => setZoom(z => Math.min(z + 0.2, 3))} title="Phóng to">🔍+</button>
          <button className="btn btn--secondary btn--sm" onClick={() => setZoom(z => Math.max(z - 0.2, 0.4))} title="Thu nhỏ">🔍-</button>
          <button className="btn btn--secondary btn--sm" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} title="Đặt lại góc nhìn">🎯 Reset</button>
        </div>
      </div>

      {/* Graph Visualizer + Node Inspector */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', background: '#090a16', borderRadius: '12px', border: '1px solid var(--border)' }}>
        
        {/* SVG Canvas */}
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onWheel={handleWheel}
        >
          {/* Subtle Grid Background */}
          <defs>
            <pattern id="graph-grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255, 255, 255, 0.03)" strokeWidth="1" />
            </pattern>
            <linearGradient id="link-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#a855f7" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#38bdf8" stopOpacity="0.2" />
            </linearGradient>
          </defs>
          <rect width="100%" height="100%" fill="url(#graph-grid)" />

          <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
            
            {/* Links */}
            {links.map((link, idx) => {
              const srcNode = nodes.find(n => n.id === link.source);
              const tgtNode = nodes.find(n => n.id === link.target);
              if (!srcNode || !tgtNode) return null;
              return (
                <line
                  key={idx}
                  x1={srcNode.x}
                  y1={srcNode.y}
                  x2={tgtNode.x}
                  y2={tgtNode.y}
                  stroke="url(#link-grad)"
                  strokeWidth="1.5"
                  strokeDasharray="3 3"
                />
              );
            })}

            {/* Nodes */}
            {nodes.map(node => {
              const isSelected = selectedNode?.id === node.id;
              return (
                <g
                  key={node.id}
                  transform={`translate(${node.x}, ${node.y})`}
                  onClick={(e) => { e.stopPropagation(); setSelectedNode(node); }}
                  style={{ cursor: 'pointer' }}
                >
                  {/* Outer glow ring if selected */}
                  {isSelected && (
                    <circle
                      r={node.radius + 8}
                      fill="none"
                      stroke={node.color}
                      strokeWidth="2.5"
                      strokeDasharray="4 2"
                      opacity="0.8"
                    />
                  )}

                  {/* Main Node Circle */}
                  <circle
                    r={node.radius}
                    fill={node.color}
                    opacity={node.type === 'vault' ? 0.9 : 0.75}
                    stroke="#ffffff"
                    strokeWidth={isSelected ? '2' : '1'}
                    style={{ transition: 'all 0.2s' }}
                  />

                  {/* Icon inside node */}
                  <text
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={node.type === 'vault' ? 14 : 9}
                    fill="#ffffff"
                    pointerEvents="none"
                  >
                    {node.type === 'vault' ? '📁' : '📄'}
                  </text>

                  {/* Label */}
                  <text
                    y={node.radius + 14}
                    textAnchor="middle"
                    fontSize="11"
                    fill={isSelected ? '#ffffff' : 'rgba(255, 255, 255, 0.7)'}
                    fontWeight={node.type === 'vault' ? 700 : 500}
                    pointerEvents="none"
                    style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
                  >
                    {node.name.length > 20 ? node.name.substring(0, 18) + '…' : node.name}
                  </text>
                </g>
              );
            })}

          </g>
        </svg>

        {/* Legend */}
        <div style={{ position: 'absolute', bottom: 16, left: 16, background: 'rgba(15, 17, 32, 0.85)', backdropFilter: 'blur(8px)', padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--border)', display: 'flex', gap: '14px', fontSize: '11.5px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#a855f7' }} /> Kho báu (Vault)
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f87171' }} /> PDF
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#34d399' }} /> Excel/CSV
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#60a5fa' }} /> Word
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#38bdf8' }} /> Khác
          </div>
        </div>

        {/* Selected Node Details Drawer */}
        {selectedNode && (
          <div style={{
            position: 'absolute',
            top: 16,
            right: 16,
            width: '300px',
            background: 'rgba(15, 17, 32, 0.95)',
            backdropFilter: 'blur(12px)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            padding: '18px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            zIndex: 10
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '24px' }}>{selectedNode.type === 'vault' ? '📁' : '📄'}</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '14px', wordBreak: 'break-word' }}>{selectedNode.name}</div>
                  <div style={{ fontSize: '11px', color: selectedNode.color, textTransform: 'uppercase', fontWeight: 600 }}>
                    {selectedNode.type === 'vault' ? 'Kho báu tri thức' : 'Tài liệu'}
                  </div>
                </div>
              </div>
              <button className="btn btn--ghost btn--sm" onClick={() => setSelectedNode(null)} style={{ padding: '2px 6px' }}>✕</button>
            </div>

            {selectedNode.type === 'doc' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                {selectedNode.size !== undefined && (
                  <div>Dung lượng: {(selectedNode.size / 1024).toFixed(1)} KB</div>
                )}
                <div>Trạng thái: <span className="badge badge--green">{selectedNode.status || 'ready'}</span></div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
              {selectedNode.type === 'doc' ? (
                <>
                  <button
                    className="btn btn--primary btn--sm"
                    onClick={() => handleStartChatWithDoc(selectedNode.id)}
                    style={{ justifyContent: 'center' }}
                  >
                    💬 Hỏi đáp AI với tài liệu này
                  </button>
                  <button
                    className="btn btn--secondary btn--sm"
                    onClick={() => navigate('/documents')}
                    style={{ justifyContent: 'center' }}
                  >
                    ✏️ Mở Thư Viện Documents
                  </button>
                </>
              ) : (
                <button
                  className="btn btn--secondary btn--sm"
                  onClick={() => { setFilterVault(selectedNode.vaultId || 'all'); setSelectedNode(null); }}
                  style={{ justifyContent: 'center' }}
                >
                  🔍 Lọc chỉ xem Kho báu này
                </button>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
